/**
 * Ambient Runtime Service (AMB-0)
 *
 * Client access to the ambient layer: background job schedules, the delivery
 * log, and the per-workspace channel policy. Jobs are executed server-side by
 * the `ambient` edge function; this service only configures and observes.
 *
 * Tables: ambient_jobs, ambient_deliveries, ambient_channel_policies
 * (migration 579). Run history: agent_run_logs with agent_type
 * 'ambient_<job_type>'. Spec: docs/9-exploration/AMBIENT-INTELLIGENCE-PRD.md §2.
 */

import { getSupabaseClient } from './supabaseClient';

// ── Types ─────────────────────────────────────────────────────────────────────

export type AmbientJobType =
  | 'sync_dispatch'
  | 'weekly_digest'
  | 'intelligence_scan'
  | 'pulse_delivery'
  | 'decision_narrative'
  | 'evidence_brief'
  | 'outcome_ledger'
  | 'beacon_scan'
  | 'review_due'
  | 'csv_import'
  | 'csv_export'
  | 'automation_sweep'
  | 'value_calibration'
  | 'catchball_overdue'
  | 'v2mom_overdue';

export const AMBIENT_JOB_META: Record<AmbientJobType, { name: string; description: string }> = {
  sync_dispatch: {
    name: 'Connector sync dispatch',
    description:
      'Pushes pending connector change events (and retries failed ones) to Jira, Linear, and Slack on a schedule instead of waiting for a manual trigger.',
  },
  weekly_digest: {
    name: 'Weekly digest',
    description:
      'A weekly summary for workspace admins — open agent findings, items that changed, and pending sync events — delivered per the channel policy.',
  },
  intelligence_scan: {
    name: 'Intelligence scan',
    description:
      'Runs Fracture, Compass, Keystone, and Meridian nightly. Each enabled agent refreshes its findings for review; high-severity findings are delivered in-app.',
  },
  pulse_delivery: {
    name: 'Pulse delivery',
    description:
      'Delivers sent stakeholder pulses to their submitters (in-app + email) — closing the loop with submitters after a priority list is published.',
  },
  decision_narrative: {
    name: 'Decision narrative',
    description:
      'Auto-writes the decision story when a priority list is approved — who decided, criteria, overrides with rationale, dissent, and Catchball history — and delivers it to the approver, cycle owner, and admins.',
  },
  evidence_brief: {
    name: 'Evidence brief',
    description:
      'Assembles what changed since ratification the moment a reprioritization opens — inform-only',
  },
  outcome_ledger: {
    name: 'Outcome ledger',
    description:
      'Records what actually happened to ratified priorities and delivers a per-approval retrospective — the calibration record',
  },
  beacon_scan: {
    name: 'Beacon scan',
    description:
      'Watches external sources for the entities the ratified plan depends on — silent unless a signal is material',
  },
  review_due: {
    name: 'Review-due scan',
    description:
      'Scans the review dates the record already carries — decisions past review_by_date, commitment terms tripped or past review, unanswered signals — and proposes reconsideration candidates. Never reopens or trips anything itself.',
  },
  csv_import: {
    name: 'CSV import schedule',
    description:
      'Runs a Spreadsheet sync import profile on a cadence (URL fetch or last uploaded file) — delta upsert by External ID, clarify queue, and an import_logs run record. Admins get an in-app notice after the run.',
  },
  csv_export: {
    name: 'CSV export schedule',
    description:
      'Runs a Spreadsheet sync export profile on a cadence — writes the configured columns, optional POST to a destination URL, and an import_logs run record. Admins get an in-app notice after the run.',
  },
  value_calibration: {
    name: 'Value calibration',
    description:
      'Closes the Value Estimator\'s learning loops nightly — marks agent estimates a person later reversed or left to expire, and asks an item\'s owner what delivered work actually took when nothing was recorded. Never changes a value.',
  },
  catchball_overdue: {
    name: 'Overdue Catchball requests',
    description:
      'Daily. When a Catchball request is past its due date, tells the people holding it the next day and then weekly, and tells the sender once. Turned off for a cycle by its "notify on overdue" setting. Never moves the ball.',
  },
  automation_sweep: {
    name: 'Automation sweep',
    description:
      'The time lane of your automation rules — fires "an item sits untouched for N days" rules and delivers the webhooks rules queued. Rules on item, cycle and agent events fire at write time without this job.',
  },
  v2mom_overdue: {
    name: 'V2MOM asks overdue',
    description:
      'A person asked to write their V2MOM against a published one and past the due date is nudged the day after and weekly after; the person who asked is told once per due date (migration 945).',
  },
};

export interface AmbientJob {
  id: string;
  workspaceId: string;
  jobType: AmbientJobType;
  enabled: boolean;
  cadenceMinutes: number;
  nextRunAt: string;
  lastRunAt: string | null;
  lastStatus: 'completed' | 'failed' | 'skipped' | null;
  lastError: string | null;
  /** Bound CSV Citizenship profile (csv_import / csv_export). */
  profileId: string | null;
}

export interface AmbientDelivery {
  id: string;
  workspaceId: string;
  userId: string | null;
  channel: 'in_app' | 'email' | 'slack';
  moment: string;
  subject: string;
  status: 'sent' | 'failed' | 'skipped';
  provider: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface AmbientChannelPolicy {
  workspaceId: string;
  inAppEnabled: boolean;
  emailDigestEnabled: boolean;
  slackEnabled: boolean;
  dailyDeliveryCap: number;
}

/** Owner-decided defaults (PRD §10 Q2): in-app + weekly email digest on, Slack opt-in. */
export function defaultChannelPolicy(workspaceId: string): AmbientChannelPolicy {
  return {
    workspaceId,
    inAppEnabled: true,
    emailDigestEnabled: true,
    slackEnabled: false,
    dailyDeliveryCap: 50,
  };
}

export interface AmbientRunLog {
  id: string;
  agentType: string;
  triggeredBy: string;
  startedAt: string;
  completedAt: string | null;
  status: string;
  stats: { processed?: number; proposed?: number; skipped?: number; errors?: number };
  errorMessage: string | null;
}

// ── Converters ────────────────────────────────────────────────────────────────

function rowToJob(row: Record<string, unknown>): AmbientJob {
  const config = (row.config as { profileId?: string } | null) ?? null;
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    jobType: row.job_type as AmbientJobType,
    enabled: row.enabled as boolean,
    cadenceMinutes: row.cadence_minutes as number,
    nextRunAt: row.next_run_at as string,
    lastRunAt: (row.last_run_at as string | null) ?? null,
    lastStatus: (row.last_status as AmbientJob['lastStatus']) ?? null,
    lastError: (row.last_error as string | null) ?? null,
    profileId: config?.profileId ?? null,
  };
}

function rowToDelivery(row: Record<string, unknown>): AmbientDelivery {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    userId: (row.user_id as string | null) ?? null,
    channel: row.channel as AmbientDelivery['channel'],
    moment: row.moment as string,
    subject: row.subject as string,
    status: row.status as AmbientDelivery['status'],
    provider: (row.provider as string | null) ?? null,
    errorMessage: (row.error_message as string | null) ?? null,
    createdAt: row.created_at as string,
  };
}

function rowToPolicy(row: Record<string, unknown>): AmbientChannelPolicy {
  return {
    workspaceId: row.workspace_id as string,
    inAppEnabled: row.in_app_enabled as boolean,
    emailDigestEnabled: row.email_digest_enabled as boolean,
    slackEnabled: row.slack_enabled as boolean,
    dailyDeliveryCap: row.daily_delivery_cap as number,
  };
}

function rowToRunLog(row: Record<string, unknown>): AmbientRunLog {
  return {
    id: row.id as string,
    agentType: row.agent_type as string,
    triggeredBy: row.triggered_by as string,
    startedAt: row.started_at as string,
    completedAt: (row.completed_at as string | null) ?? null,
    status: row.status as string,
    stats: (row.stats as AmbientRunLog['stats']) ?? {},
    errorMessage: (row.error_message as string | null) ?? null,
  };
}

// ── Queries ───────────────────────────────────────────────────────────────────

export async function getAmbientJobs(
  workspaceId: string,
): Promise<{ data: AmbientJob[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('ambient_jobs')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('job_type');
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map((r) => rowToJob(r as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function getAmbientChannelPolicy(
  workspaceId: string,
): Promise<{ data: AmbientChannelPolicy; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('ambient_channel_policies')
      .select('*')
      .eq('workspace_id', workspaceId)
      .maybeSingle();
    if (error) return { data: defaultChannelPolicy(workspaceId), error: error.message };
    if (!data) return { data: defaultChannelPolicy(workspaceId), error: null };
    return { data: rowToPolicy(data as Record<string, unknown>), error: null };
  } catch (err) {
    return {
      data: defaultChannelPolicy(workspaceId),
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

export async function getAmbientDeliveries(
  workspaceId: string,
  limit = 25,
): Promise<{ data: AmbientDelivery[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('ambient_deliveries')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) return { data: [], error: error.message };
    return {
      data: (data ?? []).map((r) => rowToDelivery(r as Record<string, unknown>)),
      error: null,
    };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function getAmbientRunLogs(
  workspaceId: string,
  limit = 10,
): Promise<{ data: AmbientRunLog[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('agent_run_logs')
      .select('*')
      .eq('workspace_id', workspaceId)
      .in('agent_type', [
        'ambient_sync_dispatch',
        'ambient_weekly_digest',
        'ambient_intelligence_scan',
        'ambient_pulse_delivery',
        'ambient_decision_narrative',
        'ambient_evidence_brief',
        'ambient_outcome_ledger',
        'ambient_beacon_scan',
        'ambient_review_due',
        'ambient_csv_import',
        'ambient_csv_export',
      ])
      .order('started_at', { ascending: false })
      .limit(limit);
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map((r) => rowToRunLog(r as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export async function setAmbientJobEnabled(input: {
  workspaceId: string;
  jobType: AmbientJobType;
  enabled: boolean;
  createdBy?: string;
}): Promise<{ data: AmbientJob | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const defaults: Record<AmbientJobType, number> = {
      sync_dispatch: 15,
      weekly_digest: 10080,
      intelligence_scan: 1440,
      pulse_delivery: 15,
      decision_narrative: 60,
      evidence_brief: 30,
      outcome_ledger: 1440,
      beacon_scan: 1440,
      review_due: 1440,
      csv_import: 1440,
      csv_export: 10080,
      automation_sweep: 15,
      value_calibration: 1440,
      catchball_overdue: 1440,
      v2mom_overdue: 1440,
    };
    const { data, error } = await supabase
      .from('ambient_jobs')
      .upsert(
        {
          workspace_id: input.workspaceId,
          job_type: input.jobType,
          enabled: input.enabled,
          cadence_minutes: defaults[input.jobType],
          created_by: input.createdBy ?? null,
        },
        { onConflict: 'workspace_id,job_type', ignoreDuplicates: false },
      )
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: rowToJob(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function saveAmbientChannelPolicy(
  policy: AmbientChannelPolicy,
  updatedBy?: string,
): Promise<{ data: AmbientChannelPolicy | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('ambient_channel_policies')
      .upsert(
        {
          workspace_id: policy.workspaceId,
          in_app_enabled: policy.inAppEnabled,
          email_digest_enabled: policy.emailDigestEnabled,
          slack_enabled: policy.slackEnabled,
          daily_delivery_cap: policy.dailyDeliveryCap,
          updated_by: updatedBy ?? null,
        },
        { onConflict: 'workspace_id' },
      )
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: rowToPolicy(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/** Invoke the ambient edge function to run one job immediately ("Run now"). */
export async function runAmbientJobNow(
  workspaceId: string,
  jobType: AmbientJobType,
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.functions.invoke('ambient', {
      body: { mode: 'run', workspace_id: workspaceId, job_type: jobType },
    });
    if (error) return { error: error.message };
    const payload = data as { error?: string } | null;
    if (payload?.error) return { error: payload.error };
    return { error: null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
